/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, type ReactNode } from "react";
import { PersonIcon } from "@/shared/ui";
import { PageHeader } from "@/features/property-management/components/PageHeader";
import { StickyHeaderBar } from "@/features/property-management/components/StickyHeaderBar";
import { ClientFilterPanel, type ClientFilters } from "../../components/ClientFilterPanel";
import { PageHeaderSortMenu, type ListOrder } from "@/features/property-management/components/page-header/PageHeaderSortMenu";
import { navIconFor } from '@layout/agency/agency-nav';

/** The profile keys the filter panel owns, all of them plain strings. */
type ClientProfilePatch = {
  incomeMin?: string;
  incomeMax?: string;
  city?: string;
  neighborhood?: string;
  state?: string;
  prefCity?: string;
  prefNeighborhood?: string;
  prefPriceMin?: string;
  prefPriceMax?: string;
  prefBusinessType?: string;
  prefPropertyKind?: string;
  prefBedroomsMin?: string;
  prefPetFriendly?: string;
};

/** Everything this header can change, as ONE partial update. */
type ClientListFilterPatch = ClientProfilePatch & {
  type?: "" | "individual" | "company";
  role?: "" | "demand" | "supply" | "both";
  broker?: string;
  imported?: boolean;
  order?: string;
};

/** Every profile key, cleared in one go by "Limpar todos". */
const PROFILE_KEYS: Array<keyof ClientProfilePatch> = [
  'incomeMin', 'incomeMax', 'city', 'neighborhood', 'state',
  'prefCity', 'prefNeighborhood', 'prefPriceMin', 'prefPriceMax',
  'prefBusinessType', 'prefPropertyKind', 'prefBedroomsMin', 'prefPetFriendly',
];

/** The patch that clears every filter this header owns, in one call. */
function clearedFilters(): ClientListFilterPatch {
  const cleared: ClientListFilterPatch = { type: "", role: "", broker: "", imported: false };
  PROFILE_KEYS.forEach((key) => { cleared[key] = ''; });
  return cleared;
}

/** One active-filter chip: its label and how to clear just that filter. */
interface FilterChip {
  label: string;
  clear: () => void;
}

/** The filter values this header renders, as the list hook holds them. */
export type ClientListHeaderFilters = ClientProfilePatch & {
  type: string;
  role: string;
  broker: string;
  imported?: boolean;
  order?: string;
};

/**
 * The profile filters that get their own chip, and the label each one wears.
 *
 * A chip says WHICH question was asked ("Mora em: Sorocaba" vs "Procura em:
 * Sorocaba"), because the two narrowings look identical once only the value
 * is shown and a broker reading the row cannot tell which list they are on.
 */
const PROFILE_CHIPS: Array<[keyof ClientProfilePatch, string]> = [
  ['incomeMin', 'filterPanel.incomeMin'],
  ['incomeMax', 'filterPanel.incomeMax'],
  ['city', 'filterPanel.city'],
  ['neighborhood', 'filterPanel.neighborhood'],
  ['state', 'filterPanel.state'],
  ['prefCity', 'filterPanel.prefCity'],
  ['prefNeighborhood', 'filterPanel.prefNeighborhood'],
  ['prefPriceMin', 'filterPanel.prefPriceMin'],
  ['prefPriceMax', 'filterPanel.prefPriceMax'],
  ['prefBusinessType', 'filterPanel.prefBusinessType'],
  ['prefPropertyKind', 'filterPanel.prefPropertyKind'],
  ['prefBedroomsMin', 'filterPanel.prefBedroomsMin'],
];

/** Capitalizes a role token for the `filterPanel.role<Role>` translation key. */
function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Resolves a broker id to its display name, falling back to the id itself. */
function brokerLabel(brokerId: string, brokers: Array<{ id: string; name: string }>): string {
  const match = brokers.find((b) => b.id === brokerId);
  return match ? match.name : brokerId;
}

/** A chip's label: the question it asked, then the value, or nothing. */
function chipLabel(question: string, value?: string): string {
  return value ? `${question}: ${value}` : '';
}

/** A chip's label for a flag: the flag's own name, or nothing. */
function flagLabel(name: string, on?: string): string {
  return on ? name : '';
}

/**
 * One chip per filled profile filter, each labelled with its own question.
 *
 * @param t - Translator.
 * @param filters - The current filter values.
 * @param setFilters - Applies a partial filter update.
 * @returns The chips, including the empty ones the caller filters out.
 */
function profileFilterChips(
  t: ClientListHeaderProps['t'],
  filters: ClientListHeaderFilters,
  setFilters: (patch: ClientListFilterPatch) => void,
): FilterChip[] {
  const chips: FilterChip[] = PROFILE_CHIPS.map(([key, labelKey]) => ({
    label: chipLabel(t(labelKey), filters[key]),
    clear: () => setFilters({ [key]: '' }),
  }));
  chips.push({
    label: flagLabel(t('filterPanel.petFriendly'), filters.prefPetFriendly),
    clear: () => setFilters({ prefPetFriendly: '' }),
  });
  return chips;
}

/**
 * Builds the active-filter chips, each knowing its own label and how to clear
 * itself -- one list drives both the "Filtros (N)" count and the removable
 * chip row, instead of two ternary chains kept in sync by hand.
 *
 * @param t - Translator.
 * @param filters - The current filter values.
 * @param brokers - The agency's brokers, for resolving a broker id to a name.
 * @param setFilters - Applies a partial filter update.
 * @returns The chips for every currently active filter.
 */
function buildFilterChips(
  t: ClientListHeaderProps['t'],
  filters: ClientListHeaderFilters,
  brokers: Array<{ id: string; name: string }>,
  setFilters: (patch: ClientListFilterPatch) => void,
): FilterChip[] {
  const candidates: FilterChip[] = [
    { label: filters.type && t(`list.types.${filters.type}`), clear: () => setFilters({ type: '' }) },
    { label: filters.role && t(`filterPanel.role${capitalize(filters.role)}`), clear: () => setFilters({ role: '' }) },
    { label: filters.broker && brokerLabel(filters.broker, brokers), clear: () => setFilters({ broker: '' }) },
    { label: filters.imported ? t('filterPanel.importedOnly') : '', clear: () => setFilters({ imported: false }) },
    ...profileFilterChips(t, filters, setFilters),
  ];
  return candidates.filter((chip) => chip.label);
}

/**
 * Every text-valued profile key, converted in one loop rather than one line
 * each: the two conversions below used to spell out all thirteen, and the
 * resulting chain of `||` was the single most complex thing in this file.
 */
type ProfileTextKey = Exclude<keyof ClientProfilePatch, 'prefPetFriendly'>;
const PROFILE_TEXT_KEYS = PROFILE_CHIPS.map(([key]) => key) as ProfileTextKey[];

/** An applied filter as the panel's draft holds it: filled, or absent. */
function draftValue(value?: string): string | undefined {
  return value ? value : undefined;
}

/** A draft value as the URL holds it: filled, or the empty default. */
function urlValue(value?: string): string {
  return value ? value : '';
}

/** The panel's own draft shape, built from the applied filters. */
function panelFilters(filters: ClientListHeaderFilters): ClientFilters {
  const texts = Object.fromEntries(
    PROFILE_TEXT_KEYS.map((key) => [key, draftValue(filters[key])]),
  ) as Pick<ClientFilters, ProfileTextKey>;
  return {
    ...texts,
    type: draftValue(filters.type) as ClientFilters['type'],
    role: draftValue(filters.role) as ClientFilters['role'],
    broker: draftValue(filters.broker),
    imported: filters.imported,
    prefPetFriendly: filters.prefPetFriendly === 'true',
  };
}

/** The panel's draft turned back into ONE patch for the URL filters. */
function appliedFilters(f: ClientFilters): ClientListFilterPatch {
  const texts = Object.fromEntries(
    PROFILE_TEXT_KEYS.map((key) => [key, urlValue(f[key])]),
  ) as Pick<ClientListFilterPatch, ProfileTextKey>;
  return {
    ...texts,
    type: urlValue(f.type) as "" | "individual" | "company",
    role: urlValue(f.role) as "" | "demand" | "supply" | "both",
    broker: urlValue(f.broker),
    imported: Boolean(f.imported),
    prefPetFriendly: f.prefPetFriendly ? 'true' : '',
  };
}

interface ClientListHeaderProps {
  t: (key: string) => string;
  /** Every filter the list is currently narrowed by. */
  filters: ClientListHeaderFilters;
  /**
   * Applies a partial filter update in ONE call.
   *
   * It replaced three independent setters, and the reason is not style.
   * Filters live in a single URL search param, and react-router documents
   * that "multiple calls to setSearchParams in the same tick will not build
   * on the prior value" -- so the old `setTypeFilter(...); setRoleFilter(...);
   * setBrokerFilter(...)` sequence kept only the last write. Applying
   * "Pessoa Jurídica" fired type='company', then role='', then broker='',
   * and the broker write -- computed from the pre-apply snapshot -- landed
   * last with every key at its default, which the hook encodes as "no
   * filters" and strips from the URL. The panel closed, the URL stayed
   * clean, and the list never filtered.
   */
  setFilters: (patch: ClientListFilterPatch) => void;
  setSearch: (v: string) => void;
  onAddClient: () => void;
  brokers: Array<{ id: string; name: string }>;
  /** Rendered to the left of "Novo cliente"; the spreadsheet menu passes through here. */
  secondaryAction?: ReactNode;
}

/**
 * Header for the manage-clients list: search, filters, ordering and the add
 * action.
 *
 * @param props - Translator, the active filters and their setter, search
 *   setter, and add callback.
 * @returns The rendered client list header.
 */
export function ClientListHeader({
  t, filters, setFilters, setSearch, onAddClient, brokers, secondaryAction,
}: ClientListHeaderProps) {
  const [showFilters, setShowFilters] = useState(false);

  const chips = buildFilterChips(t, filters, brokers, setFilters);
  const removeFilter = (label: string) => {
    chips.find((chip) => chip.label === label)?.clear();
  };

  return (
    <>
      <StickyHeaderBar>
        <PageHeader
          title={t('list.title') || 'Clientes'}
          icon={navIconFor('clients')}
          ctaLabel={t('list.newClient') || 'Novo Cliente'}
          secondaryAction={secondaryAction}
          searchPlaceholder={t('list.searchPlaceholder') || 'Buscar clientes...'}
          onCtaClick={onAddClient}
          onSearchChange={(val) => setSearch(val)}
          onFiltersToggle={() => setShowFilters(!showFilters)}
          filterCount={chips.length}
          activeFilters={chips.map((chip) => chip.label)}
          onRemoveFilter={removeFilter}
          onClearAllFilters={() => setFilters(clearedFilters())}
          introLabel={t('intro.label')}
          introBody={t('intro.body')}
          introTestIdPrefix="clients"
          trailingAction={(
            <PageHeaderSortMenu
              testIdPrefix="clients"
              order={(filters.order === 'asc' ? 'asc' : 'desc') as ListOrder}
              onChange={(order) => setFilters({ order })}
            />
          )}
        />
      </StickyHeaderBar>

      <ClientFilterPanel
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onApply={(f: ClientFilters) => setFilters(appliedFilters(f))}
        onClear={() => setFilters(clearedFilters())}
        initialFilters={panelFilters(filters)}
        brokers={brokers}
      />
    </>
  );
}
